import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, onSnapshot, writeBatch, getDoc, setDoc, deleteDoc, where, increment } from 'firebase/firestore';
import { db, auth } from '../firebase';
import '../theme-palette.css';
import { useOutletContext } from 'react-router-dom';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import { Repeat, BarChart2, UserMinus, UserCheck, Settings, Calendar as CalendarIcon, Save, Plus, Trash2, Download, Upload, X } from 'lucide-react';
import Papa from 'papaparse';

const SubstitutionAdmin = () => {
  const { t } = useT();
  const { activeSchoolId, schoolMetadata, isMobile, closeSidebar } = useOutletContext();
  const [activeTab, setActiveTab] = useState('statistics');

  // Both logging forms used to sit permanently above their history table, pushing it down the page.
  // They are dialogs now, opened from the tab-bar actions.
  const [showAbsenceForm, setShowAbsenceForm] = useState(false);
  const [showDutyForm, setShowDutyForm] = useState(false);

  // Shared dialog behaviour: retract the mobile sidebar (a fixed overlay that would otherwise sit on
  // top of the sheet), stop the page behind from scrolling, and close on Escape.
  useEffect(() => {
    const open = showAbsenceForm || showDutyForm;
    if (!open) return;
    if (isMobile) closeSidebar?.();

    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setShowAbsenceForm(false);
      setShowDutyForm(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showAbsenceForm, showDutyForm, isMobile, closeSidebar]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Data states
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  
  // Settings state
  const [settings, setSettings] = useState({
    absenceReasons: [
      { id: 'full', reason: 'Full Day', defaultDeduction: 6 },
      { id: 'am', reason: 'AM Half Day', defaultDeduction: 4 },
      { id: 'pm', reason: 'PM Half Day', defaultDeduction: 2 }
    ],
    dutyTypes: [
      { id: 'extra_duty', type: 'Extra Duty', defaultReward: 1 }
    ],
    defaultSubReward: 1
  });

  const [absences, setAbsences] = useState([]);
  const [substitutions, setSubstitutions] = useState([]);
  const [emailByInitial, setEmailByInitial] = useState({});
  const [profiles, setProfiles] = useState([]);
  
  const [absenceForm, setAbsenceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    staffId: '',
    reasonId: '',
    deduction: 0,
    remarks: ''
  });

  const [dutyForm, setDutyForm] = useState({
    date: new Date().toISOString().split('T')[0],
    staffId: '',
    typeId: 'extra_duty',
    remarks: '',
    reward: 1
  });

  const [arrangeDate, setArrangeDate] = useState(new Date().toISOString().split('T')[0]);
  const [dayCode, setDayCode] = useState('');
  const [orphanedLessons, setOrphanedLessons] = useState([]);
  const [hiddenLessons, setHiddenLessons] = useState([]);
  const [substituteAssignments, setSubstituteAssignments] = useState({});

  const [statsDate, setStatsDate] = useState(new Date().toISOString().split('T')[0]);
  const [statsDayCode, setStatsDayCode] = useState('');
  const [allTeacherLessons, setAllTeacherLessons] = useState([]);
  const [timeRange, setTimeRange] = useState('5days');

  // Drag and Drop Substitutions
  const [dragDropSubstitutions, setDragDropSubstitutions] = useState({});
  // One tint per substitution so a teacher's replacements are scannable down the grid. Literal for
  // the same reason as the sender colours: they are a set that must stay mutually distinct, not
  // brand colours.
  const substitutionColors = ['#BFDBFE', '#FBCFE8', '#FDE68A', '#FED7AA', '#E9D5FF', '#BBF7D0', '#FECACA', '#D9F99D', '#BAE6FD', '#DDD6FE'];

  useEffect(() => {
    if (activeSchoolId) {
      fetchData();
    }
  }, [activeSchoolId]);

  useEffect(() => {
    if (activeSchoolId) {
      let startDateStr = '';
      if (timeRange === '5days') {
        const d = new Date();
        d.setDate(d.getDate() - 4);
        startDateStr = d.toISOString().split('T')[0];
      } else if (timeRange === 'month') {
        const d = new Date();
        d.setDate(1);
        startDateStr = d.toISOString().split('T')[0];
      }

      let absQuery = collection(db, 'schools', activeSchoolId, 'staff_absences');
      if (startDateStr) {
        absQuery = query(absQuery, where('date', '>=', startDateStr));
      }
      
      const unsubAbs = onSnapshot(absQuery, snap => {
        const list = [];
        snap.forEach(d => list.push({ absId: d.id, ...d.data() }));
        list.sort((a, b) => {
          if (a.date === b.date) {
            return (a.staffId || '').localeCompare(b.staffId || '');
          }
          return b.date.localeCompare(a.date);
        });
        setAbsences(list);
      });

      let subQuery = collection(db, 'schools', activeSchoolId, 'lesson_substitutions');
      if (startDateStr) {
        subQuery = query(subQuery, where('date', '>=', startDateStr));
      }

      const unsubSub = onSnapshot(subQuery, snap => {
        const list = [];
        snap.forEach(d => list.push({ subId: d.id, ...d.data() }));
        list.sort((a, b) => {
          if (a.date === b.date) {
            return (a.substituteStaffId || '').localeCompare(b.substituteStaffId || '');
          }
          return b.date.localeCompare(a.date);
        });
        setSubstitutions(list);
      });

      return () => {
        unsubAbs();
        unsubSub();
      };
    }
  }, [activeSchoolId, timeRange]);

  useEffect(() => {
    setDutyForm(prev => ({ ...prev, reward: settings.defaultSubReward || 1 }));
  }, [settings.defaultSubReward]);

  useEffect(() => {
    const fetchArrangeData = async () => {
      if (!activeSchoolId || activeTab !== 'arrange') return;

      try {
        const eSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'calendar_events'), where('date', '==', arrangeDate)));
        let currentDayCode = '';
        if (!eSnap.empty) {
          const rawCycleDay = eSnap.docs[0].data().cycleDay;
          if (rawCycleDay) {
            if (rawCycleDay.startsWith('Day ')) {
              currentDayCode = rawCycleDay.replace('Day ', '');
            } else {
              const dayMap = { 'Monday': 'M', 'Tuesday': 'T', 'Wednesday': 'W', 'Thursday': 'H', 'Friday': 'F' };
              currentDayCode = dayMap[rawCycleDay] || '';
            }
          }
        }
        setDayCode(currentDayCode);

        if (!currentDayCode) {
          setOrphanedLessons([]);
          return;
        }

        const qSub = query(collection(db, 'schools', activeSchoolId, 'lesson_substitutions'), where('date', '==', arrangeDate));
        const subSnap = await getDocs(qSub);
        const subs = [];
        subSnap.forEach(d => subs.push({ subId: d.id, ...d.data() }));

        const absentStaff = absences.filter(a => a.date === arrangeDate).map(a => a.staffId);
        
        if (absentStaff.length === 0) {
          setOrphanedLessons([]);
          return;
        }

        let currentAllTeacherLessons = allTeacherLessons;
        if (currentAllTeacherLessons.length === 0) {
          const tSnapAll = await getDocs(collection(db, 'schools', activeSchoolId, 'teacher_lessons'));
          const lessonsAll = [];
          tSnapAll.forEach(doc => lessonsAll.push({ id: doc.id, ...doc.data() }));
          setAllTeacherLessons(lessonsAll);
          currentAllTeacherLessons = lessonsAll;
        }

        const lessons = [];
        const initAssignments = {};
        currentAllTeacherLessons.forEach(data => {
          if (absentStaff.includes(data.T) && data.P && data.P.startsWith(currentDayCode)) {
            // Check if it's already substituted
            const existingSub = subs.find(s => s.periodCode === data.P && s.type === 'lesson' && s.absentStaffId === data.T);
            lessons.push(data);
            if (existingSub) {
              initAssignments[data.id] = { substitute: existingSub.substituteStaffId || '', room: existingSub.room || data.R || '', subId: existingSub.subId };
            } else {
              initAssignments[data.id] = { substitute: '', room: data.R || '' };
            }
          }
        });
        
        setOrphanedLessons(lessons);
        setSubstituteAssignments(initAssignments);
      } catch (err) {
        console.error(err);
      }
    };
    
    fetchArrangeData();
  }, [arrangeDate, activeSchoolId, activeTab, absences]);

  useEffect(() => {
    const fetchStatsData = async () => {
      if (!activeSchoolId || activeTab !== 'statistics') return;
      try {
        const eSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'calendar_events'), where('date', '==', statsDate)));
        let currentDayCode = '';
        if (!eSnap.empty) {
          const rawCycleDay = eSnap.docs[0].data().cycleDay;
          if (rawCycleDay) {
            if (rawCycleDay.startsWith('Day ')) {
              currentDayCode = rawCycleDay.replace('Day ', '');
            } else {
              const dayMap = { 'Monday': 'M', 'Tuesday': 'T', 'Wednesday': 'W', 'Thursday': 'H', 'Friday': 'F' };
              currentDayCode = dayMap[rawCycleDay] || '';
            }
          }
        }
        setStatsDayCode(currentDayCode);

        // Fetch all teacher lessons once if not already fetched
        if (allTeacherLessons.length === 0) {
          const tSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'teacher_lessons'));
          const lessons = [];
          tSnap.forEach(d => lessons.push({ id: d.id, ...d.data() }));
          setAllTeacherLessons(lessons);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchStatsData();
  }, [statsDate, activeSchoolId, activeTab, allTeacherLessons.length]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Teachers
      const sSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'staff_profiles')));
      // Built from every profile, unlike tList below which keeps only teaching staff. Both CSV
      // imports stamp an address next to the initial, and a lookup that could miss somebody would
      // reintroduce exactly the records migrateDutyIdentity exists to remove.
      const emailMap = {};
      sSnap.forEach(d => {
        const ini = String((d.data() || {}).initial || '').trim();
        if (ini) emailMap[ini] = String(d.id || '').toLowerCase();
      });
      setEmailByInitial(emailMap);
      const tList = [];
      sSnap.forEach(d => {
        const prof = d.data();
        const role = (prof.role || prof.Role || '').toLowerCase();
        if (role === 'teacher' || role === 'tch' || role === 'admin') {
          const initial = prof.initial || d.id.split('@')[0].toUpperCase();
          const teacherName = prof.displayName || (prof.lastName ? `${prof.lastName || ''} ${prof.firstName || ''}`.trim() : d.id.split('@')[0]);
          tList.push({ uid: d.id, name: `${initial} - ${teacherName}`, initial: initial });
        }
      });
      
      // We will setTeachers after fetching balances below
      // 2. Fetch Settings
      const setSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_settings'));
      if (setSnap.exists()) {
        setSettings(prev => ({ ...prev, ...setSnap.data() }));
      }

      // 3. Fetch Timetable Profiles
      const pSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'timetable_profiles')));
      const pList = [];
      pSnap.forEach(d => pList.push({ uid: d.id, ...d.data() }));
      setProfiles(pList);

      // 4. Fetch System Settings for Rooms
      const sysRef = doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings');
      const sysSnap = await getDoc(sysRef);
      if (sysSnap.exists() && sysSnap.data().dropdowns) {
        setRooms(sysSnap.data().dropdowns.locations || []);
      } else if (schoolMetadata?.dropdowns) {
        setRooms(schoolMetadata.dropdowns.locations || []);
      }

      // 4. Fetch Balances
      const balSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances'));
      const balancesMap = balSnap.exists() ? balSnap.data() : {};
      
      tList.forEach(teacher => {
        teacher.balance = balancesMap[teacher.initial] || 0;
      });
      
      setTeachers(tList.sort((a, b) => a.initial.localeCompare(b.initial)));

    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_settings'), settings);
      alert(t('substitutionAdmin.settingsSaved'));
    } catch (e) {
      alert(t('substitutionAdmin.settingsFailed', { message: e.message }));
    }
    setSaving(false);
  };

  const handleReasonChange = (e) => {
    const rId = e.target.value;
    const reasonObj = settings.absenceReasons.find(r => r.id === rId);
    setAbsenceForm(prev => ({
      ...prev,
      reasonId: rId,
      deduction: reasonObj ? reasonObj.defaultDeduction : 0
    }));
  };

  const handleLogAbsence = async (e) => {
    e.preventDefault();
    if (!absenceForm.staffId || !absenceForm.reasonId) return alert(t('substitutionAdmin.staffAndReasonRequired'));
    
    setSaving(true);
    try {
      const newRef = doc(collection(db, 'schools', activeSchoolId, 'staff_absences'));
      await setDoc(newRef, {
        date: absenceForm.date,
        staffId: absenceForm.staffId,
        reasonId: absenceForm.reasonId,
        deduction: Number(absenceForm.deduction),
        remarks: absenceForm.remarks
      });
      
      const balancesRef = doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances');
      await setDoc(balancesRef, {
        [absenceForm.staffId]: increment(-Number(absenceForm.deduction))
      }, { merge: true });
      
      // Update local state instantly so UI reflects
      setTeachers(prev => prev.map(teacher => teacher.initial === absenceForm.staffId ? { ...teacher, balance: teacher.balance - Number(absenceForm.deduction) } : teacher));
      
      alert(t('substitutionAdmin.absenceLogged'));
      setAbsenceForm(prev => ({ ...prev, staffId: '', reasonId: '', deduction: 0, remarks: '' }));
    } catch (err) {
      alert(t('substitutionAdmin.absenceFailed', { message: err.message }));
    }
    setSaving(false);
  };

  // Takes the record, not its id. The button used to pass `abs.absId` into a parameter this
  // destructures as an object, so absId came out undefined and Firestore threw on the path segment
  // -- "Cannot read properties of undefined (reading 'indexOf')". Deleting an absence had never
  // worked. It needs the whole record anyway: reversing the deduction is impossible from an id.
  const handleDeleteAbsence = async (abs) => {
    if (!window.confirm(t('substitutionAdmin.deleteAbsenceConfirm'))) return;
    try {
      const { absId, staffId, deduction } = abs || {};
      if (!absId) { alert(t('substitutionAdmin.absenceNoId')); return; }
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'staff_absences', absId));
      
      const balancesRef = doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances');
      await setDoc(balancesRef, {
        [staffId]: increment(Number(deduction))
      }, { merge: true });
      
      // Update local state
      setTeachers(prev => prev.map(teacher => teacher.initial === staffId ? { ...teacher, balance: teacher.balance + Number(deduction) } : teacher));
    } catch (err) {
      alert(t('substitutionAdmin.deleteAbsenceFailed', { message: err.message }));
    }
  };

  const handleExportAbsence = () => {
    const csvData = absences.map(a => ({
      absId: a.absId || '',
      Date: a.date,
      StaffInitial: a.staffId,
      ReasonID: a.reasonId,
      Deduction: a.deduction,
      Remarks: a.remarks || ''
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'absences_export.csv';
    link.click();
  };

  const handleImportAbsence = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSaving(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          // Importing absences wrote the records and left the balance alone, exactly as the
          // substitution import did on the other side of the ledger. A term of leave loaded from a
          // spreadsheet therefore deducted nothing, and the two numbers a teacher now sees on their
          // own Substitution tab -- days absent, and balance -- disagreed with no way to tell why.
          //
          // Deltas rather than a flat deduction: a row carrying an absId is an UPDATE of a record
          // that already took points off somebody, so the old amount goes back before the new one
          // comes off, and a row moved to a different teacher credits the first one.
          const existingAbsById = new Map(absences.map(x => [x.absId, x]));
          const deltas = {};
          const bump = (ini, n) => {
            if (!ini || !n) return;
            deltas[ini] = (deltas[ini] || 0) + n;
          };

          const writes = [];
          const unknownInitials = new Set();
          for (const row of results.data) {
            const date = (row.Date || '').trim();
            const staff = (row.StaffInitial || '').trim();
            if (!date || !staff) continue;

            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(date)) {
              alert(t('substitutionAdmin.badDate', { date }));
              setSaving(false);
              e.target.value = '';
              return;
            }

            const absId = (row.absId || '').trim();
            const ref = absId ? doc(db, 'schools', activeSchoolId, 'staff_absences', absId) : doc(collection(db, 'schools', activeSchoolId, 'staff_absences'));
            const deduction = Number(row.Deduction) || 0;

            const prev = absId ? existingAbsById.get(absId) : null;
            if (prev) bump(prev.staffId, Number(prev.deduction || 0));
            bump(staff, -deduction);

            // Stamped on the way in, or importing would undo the migration one row at a time: a
            // record carrying only an initial is one the security rules cannot speak about.
            const staffEmail = emailByInitial[staff] || '';
            if (staff && !staffEmail) unknownInitials.add(staff);

            writes.push({
              ref,
              data: {
                date: date,
                staffId: staff,
                staffEmail,
                reasonId: (row.ReasonID || '').trim(),
                deduction,
                remarks: (row.Remarks || '').trim()
              },
            });
          }

          if (writes.length > 0) {
            // Chunked at 400. A batch caps at 500 and the old loop had no limit, so a whole year in
            // one file threw and imported nothing.
            for (let i = 0; i < writes.length; i += 400) {
              const batch = writeBatch(db);
              writes.slice(i, i + 400).forEach(w => batch.set(w.ref, w.data, { merge: true }));
              await batch.commit();
            }
            const balPatch = {};
            Object.entries(deltas).forEach(([ini, n]) => { if (n) balPatch[ini] = increment(n); });
            if (Object.keys(balPatch).length > 0) {
              await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances'), balPatch, { merge: true });
            }
            alert(
              t('substitutionAdmin.importedAbsences', {
                records: writes.length,
                teachers: Object.values(deltas).filter(Boolean).length,
              })
              + (unknownInitials.size
                ? t('substitutionAdmin.unknownInitials', { initials: [...unknownInitials].join(', ') })
                : '')
            );
            fetchData();
          }
        } catch (err) {
          alert(t('substitutionAdmin.importAbsencesFailed', { message: err.message }));
        }
        setSaving(false);
        e.target.value = '';
      }
    });
  };

  const handleExportSubstitution = () => {
    const csvData = substitutions.map(s => ({
      subId: s.subId || '',
      Date: s.date,
      SubstituteStaffInitial: s.substituteStaffId,
      Type: s.type,
      Reward: s.reward,
      AbsentStaffInitial: s.absentStaffId || '',
      Class: s.class || '',
      Subject: s.subj || '',
      Room: s.room || '',
      PeriodCode: s.periodCode || '',
      StartTime: s.start_time || '',
      EndTime: s.end_time || '',
      Remarks: s.remarks || ''
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'substitutions_export.csv';
    link.click();
  };

  const handleImportSubstitution = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSaving(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          // Every other way of recording a cover duty moves the balance: logging one adds the
          // reward, deleting one takes it back, reassigning one moves it between two people. This
          // path wrote the records and left the accumulator alone, so a school that arranges cover
          // by spreadsheet gave its staff duties that were never paid for -- shown on their own
          // Substitution tab as lessons covered against a balance that had not moved.
          //
          // Deltas, not a flat credit. A row carrying a subId is an UPDATE of a record that already
          // credited somebody: the reward may have changed, or the lesson handed to a different
          // teacher, and both mean taking it off the previous holder first.
          const existingById = new Map(substitutions.map(x => [x.subId, x]));
          const deltas = {};
          const bump = (ini, n) => {
            if (!ini || !n) return;
            deltas[ini] = (deltas[ini] || 0) + n;
          };

          const writes = [];
          const unknownInitials = new Set();
          for (const row of results.data) {
            const date = (row.Date || '').trim();
            const subStaff = (row.SubstituteStaffInitial || '').trim();
            if (!date || !subStaff) continue;

            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(date)) {
              alert(t('substitutionAdmin.badDate', { date }));
              setSaving(false);
              e.target.value = '';
              return;
            }

            const subId = (row.subId || '').trim();
            const ref = subId ? doc(db, 'schools', activeSchoolId, 'lesson_substitutions', subId) : doc(collection(db, 'schools', activeSchoolId, 'lesson_substitutions'));
            const reward = Number(row.Reward) || 0;
            const absStaff = (row.AbsentStaffInitial || '').trim();

            const prev = subId ? existingById.get(subId) : null;
            if (prev) bump(prev.substituteStaffId, -Number(prev.reward || 0));
            bump(subStaff, reward);

            const subEmail = emailByInitial[subStaff] || '';
            const absEmail = absStaff ? (emailByInitial[absStaff] || '') : '';
            if (subStaff && !subEmail) unknownInitials.add(subStaff);
            if (absStaff && !absEmail) unknownInitials.add(absStaff);

            writes.push({
              ref,
              data: {
                date: date,
                substituteStaffId: subStaff,
                substituteEmail: subEmail,
                type: (row.Type || 'extra_duty').trim(),
                reward,
                absentStaffId: absStaff,
                absentEmail: absEmail,
                class: (row.Class || '').trim(),
                subj: (row.Subject || '').trim(),
                room: (row.Room || '').trim(),
                periodCode: (row.PeriodCode || '').trim(),
                start_time: (row.StartTime || '').trim(),
                end_time: (row.EndTime || '').trim(),
                remarks: (row.Remarks || '').trim(),
                status: 'arranged'
              },
            });
          }

          if (writes.length > 0) {
            for (let i = 0; i < writes.length; i += 400) {
              const batch = writeBatch(db);
              writes.slice(i, i + 400).forEach(w => batch.set(w.ref, w.data, { merge: true }));
              await batch.commit();
            }

            // Last, and on its own. Committed first, a failure part way through the records would
            // leave people credited for duties that do not exist; committed last, the worst case is
            // the state this import was in before today -- and the catch below says so rather than
            // reporting success.
            const balPatch = {};
            Object.entries(deltas).forEach(([ini, n]) => { if (n) balPatch[ini] = increment(n); });
            if (Object.keys(balPatch).length > 0) {
              await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances'), balPatch, { merge: true });
            }
            alert(
              t('substitutionAdmin.importedSubstitutions', {
                records: writes.length,
                teachers: Object.values(deltas).filter(Boolean).length,
              })
              + (unknownInitials.size
                ? t('substitutionAdmin.unknownInitials', { initials: [...unknownInitials].join(', ') })
                : '')
            );
            fetchData();
          }
        } catch (err) {
          alert(t('substitutionAdmin.importSubsFailed', { message: err.message }));
        }
        setSaving(false);
        e.target.value = '';
      }
    });
  };

  const handleLogDuty = async (e) => {
    e.preventDefault();
    if (!dutyForm.staffId) return alert(t('substitutionAdmin.staffRequired'));
    
    setSaving(true);
    try {
      const newRef = doc(collection(db, 'schools', activeSchoolId, 'lesson_substitutions'));
      await setDoc(newRef, {
        date: dutyForm.date,
        substituteStaffId: dutyForm.staffId,
        reward: Number(dutyForm.reward),
        remarks: dutyForm.remarks,
        type: dutyForm.typeId || 'extra_duty',
        status: 'completed'
      });
      
      const balancesRef = doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances');
      await setDoc(balancesRef, {
        [dutyForm.staffId]: increment(Number(dutyForm.reward))
      }, { merge: true });
      
      setTeachers(prev => prev.map(teacher => teacher.initial === dutyForm.staffId ? { ...teacher, balance: teacher.balance + Number(dutyForm.reward) } : teacher));
      
      alert(t('substitutionAdmin.dutyLogged'));
      setDutyForm(prev => ({ ...prev, staffId: '', remarks: '' }));
    } catch (err) {
      alert(t('substitutionAdmin.dutyFailed', { message: err.message }));
    }
    setSaving(false);
  };

  // Also takes the record rather than its id, and for the same reason: recording a cover duty
  // credits its reward, so deleting one has to take the reward back. It did not, which left the
  // balance permanently ahead of the records behind it -- the third way this accumulator leaked,
  // alongside the two importers.
  const handleDeleteSub = async (sub) => {
    if (!window.confirm(t('substitutionAdmin.deleteRecordConfirm'))) return;
    const { subId, substituteStaffId, reward } = sub || {};
    if (!subId) { alert(t('substitutionAdmin.recordNoId')); return; }
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'lesson_substitutions', subId));
      if (substituteStaffId && Number(reward)) {
        await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances'), {
          [substituteStaffId]: increment(-Number(reward)),
        }, { merge: true });
        setTeachers(prev => prev.map(teacher => (
          teacher.initial === substituteStaffId ? { ...teacher, balance: teacher.balance - Number(reward) } : teacher
        )));
      }
    } catch (err) {
      alert(t('substitutionAdmin.deleteRecordFailed', { message: err.message }));
    }
  };

  const handleAddAbsenceReason = () => {
    setSettings(prev => ({
      ...prev,
      absenceReasons: [...prev.absenceReasons, { id: Date.now().toString(), reason: 'New Reason', defaultDeduction: 1 }]
    }));
  };

  const handleUpdateAbsenceReason = (id, field, value) => {
    setSettings(prev => ({
      ...prev,
      absenceReasons: prev.absenceReasons.map(r => r.id === id ? { ...r, [field]: value } : r)
    }));
  };

  const handleRemoveAbsenceReason = (id) => {
    setSettings(prev => ({
      ...prev,
      absenceReasons: prev.absenceReasons.filter(r => r.id !== id)
    }));
  };

  const handleAddDutyType = () => {
    setSettings(prev => ({
      ...prev,
      dutyTypes: [...(prev.dutyTypes || []), { id: Date.now().toString(), type: 'New Duty Type', defaultReward: 1 }]
    }));
  };

  const handleUpdateDutyType = (id, field, value) => {
    setSettings(prev => ({
      ...prev,
      dutyTypes: (prev.dutyTypes || []).map(r => r.id === id ? { ...r, [field]: value } : r)
    }));
  };

  const handleRemoveDutyType = (id) => {
    setSettings(prev => ({
      ...prev,
      dutyTypes: (prev.dutyTypes || []).filter(r => r.id !== id)
    }));
  };

  // Which lesson is waiting to be placed. Tap it, then tap where it goes.
  //
  // Dragging stays, but it cannot be the only way: on an iPad a drag is a text selection unless the
  // user knows to press and hold first, so the feature was effectively unavailable on the device
  // the office actually carries around. Tap-then-tap needs no gesture at all and is faster with a
  // mouse too -- the same reason the seating plan and the SEN rooms work this way.
  const [pickedLesson, setPickedLesson] = useState(null);

  // The one place a substitution is made. Both the drop and the tap call this, so the two paths
  // cannot drift into doing subtly different things.
  // A lesson picked up on one day means nothing on another, and holding it across a date change
  // would let the next tap place a lesson from a grid that is no longer on screen.
  useEffect(() => { setPickedLesson(null); }, [statsDate, arrangeDate, activeTab]);

  const placeSubstitution = (data, targetTeacherId, targetPeriodIdx) => {
    if (data.periodIdx !== targetPeriodIdx) return false;

    // Put back on the teacher it came from: that is how an arrangement is undone.
    if (data.sourceTeacherId === targetTeacherId) {
      setDragDropSubstitutions(prev => {
        const newObj = { ...prev };
        delete newObj[`${data.sourceTeacherId}_${targetPeriodIdx}`];
        return newObj;
      });
      return true;
    }

    setDragDropSubstitutions(prev => {
      const nextId = Object.keys(prev).length;
      const color = substitutionColors[nextId % substitutionColors.length];
      return {
        ...prev,
        [`${data.sourceTeacherId}_${targetPeriodIdx}`]: {
          substituteId: targetTeacherId,
          lesson: data.lesson,
          color,
        },
      };
    });
    return true;
  };

  const handleDragStart = (e, teacherId, periodIdx, lessonObj) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      sourceTeacherId: teacherId,
      periodIdx: periodIdx,
      lesson: lessonObj
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetTeacherId, targetPeriodIdx) => {
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (!placeSubstitution(data, targetTeacherId, targetPeriodIdx)) {
        alert(t('substitutionAdmin.dropSameColumn'));
      }
      setPickedLesson(null);
    } catch (err) {
      console.error(err);
    }
  };

  // A tap on a cell. Which of the two things it means depends on whether something is waiting:
  // nothing picked yet, so this is the lesson to move; something picked, so this is where it goes.
  const handleCellTap = ({ isDraggable, isDroppable, data, teacherId, periodIdx }) => {
    if (pickedLesson) {
      // Tapping the same cell again puts it down without doing anything -- an escape that needs no
      // separate control and no explanation.
      if (pickedLesson.sourceTeacherId === teacherId && pickedLesson.periodIdx === periodIdx) {
        setPickedLesson(null);
        return;
      }
      if (isDroppable && placeSubstitution(pickedLesson, teacherId, periodIdx)) {
        setPickedLesson(null);
        return;
      }
      // A tap somewhere that cannot take it: pick up the new lesson if that is what was tapped,
      // otherwise leave the selection alone rather than silently losing it.
      if (isDraggable) setPickedLesson(data);
      return;
    }
    if (isDraggable) setPickedLesson(data);
  };

  const handleSaveDragDrops = async () => {
    setSaving(true);
    try {
      const batch = writeBatch(db);
      const entries = Object.entries(dragDropSubstitutions);
      
      for (const [key, sub] of entries) {
        const [absentStaffId, pIdxStr] = key.split('_');
        const newRef = doc(collection(db, 'schools', activeSchoolId, 'lesson_substitutions'));
        
        // Find start/end time
        let startTime = '';
        let endTime = '';
        let periodSlot = pIdxStr;

        if (pIdxStr && profiles.length > 0) {
          const pIdx = parseInt(pIdxStr);
          const defProfile = profiles.find(p => p.isDefault) || profiles[0];
          if (defProfile && defProfile.periods && defProfile.periods[pIdx]) {
            startTime = defProfile.periods[pIdx].startTime || '';
            endTime = defProfile.periods[pIdx].endTime || '';
            let extractedSlot = (defProfile.periods[pIdx].name || '').toString().replace(/\D/g, '');
            if (extractedSlot) periodSlot = extractedSlot;
          }
        }

        const periodCode = `${statsDayCode}${periodSlot}`;

        batch.set(newRef, {
          date: statsDate,
          absentStaffId: absentStaffId,
          substituteStaffId: sub.substituteId,
          type: 'lesson',
          periodCode: periodCode,
          class: sub.lesson.detail.split(' ')[0] || '',
          subj: sub.lesson.detail.split(' ')[1] || '',
          room: sub.lesson.room || '',
          start_time: startTime,
          end_time: endTime,
          reward: settings.defaultSubReward || 1,
          remarks: 'Drag & Drop Assigned',
          color: sub.color
        });
      }
      
      await batch.commit();
      setDragDropSubstitutions({});
      setPickedLesson(null);
    } catch (err) {
      alert(t('substitutionAdmin.saveSubsFailed', { message: err.message }));
    }
    setSaving(false);
  };

  const handleIgnoreLesson = (lesson) => {
    if (!window.confirm(t('substitutionAdmin.deleteLessonConfirm', { lesson: `${lesson.C || ''} ${lesson.S || ''}`.trim() }))) return;
    setHiddenLessons(prev => [...prev, lesson.id]);
  };

  const handleSaveAllAssignments = async () => {
    setSaving(true);
    try {
      const batch = writeBatch(db);
      let hasChanges = false;
      let colorIndex = 0;
      
      for (const lesson of orphanedLessons) {
        const assignment = substituteAssignments[lesson.id];
        const existingSubId = assignment?.subId;
        
        if (assignment && assignment.substitute) {
          hasChanges = true;
          // Find start/end time
          let startTime = '';
          let endTime = '';
          const pIdxStr = lesson.P.split('_')[1] || lesson.P.replace(/\D/g, '');
          if (pIdxStr && profiles.length > 0) {
            const pIdx = parseInt(pIdxStr);
            const defProfile = profiles.find(p => p.isDefault) || profiles[0];
            if (defProfile && defProfile.periods && defProfile.periods[pIdx]) {
              startTime = defProfile.periods[pIdx].startTime || '';
              endTime = defProfile.periods[pIdx].endTime || '';
            }
          }
          if (existingSubId) {
            const existingRecord = substitutions.find(s => s.subId === existingSubId);
            const docRef = doc(db, 'schools', activeSchoolId, 'lesson_substitutions', existingSubId);
            batch.update(docRef, {
              substituteStaffId: assignment.substitute,
              room: assignment.room || lesson.R || '',
              color: existingRecord?.color || substitutionColors[colorIndex % substitutionColors.length]
            });
            
            // Check if substitute changed
            if (existingRecord && existingRecord.substituteStaffId !== assignment.substitute) {
               const balancesRef = doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances');
               batch.set(balancesRef, {
                 [existingRecord.substituteStaffId]: increment(-Number(existingRecord.reward || 0)),
                 [assignment.substitute]: increment(Number(existingRecord.reward || 0)) // Assuming new sub gets same reward
               }, { merge: true });
               
               // Update local memory
               setTeachers(prev => prev.map(teacher => {
                 if (teacher.initial === existingRecord.substituteStaffId) return { ...teacher, balance: teacher.balance - Number(existingRecord.reward || 0) };
                 if (teacher.initial === assignment.substitute) return { ...teacher, balance: teacher.balance + Number(existingRecord.reward || 0) };
                 return teacher;
               }));
            }
          } else {
            const newRef = doc(collection(db, 'schools', activeSchoolId, 'lesson_substitutions'));
            batch.set(newRef, {
              date: arrangeDate,
              substituteStaffId: assignment.substitute,
              reward: settings.defaultSubReward || 1,
              type: 'lesson',
              class: lesson.C || '',
              subj: lesson.S || '',
              room: assignment.room || lesson.R || '',
              start_time: startTime,
              end_time: endTime,
              periodCode: lesson.P,
              status: 'arranged',
              absentStaffId: lesson.T,
              color: substitutionColors[colorIndex % substitutionColors.length]
            });
            
            const balancesRef = doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances');
            batch.set(balancesRef, {
              [assignment.substitute]: increment(Number(settings.defaultSubReward || 1))
            }, { merge: true });
            
            setTeachers(prev => prev.map(teacher => teacher.initial === assignment.substitute ? { ...teacher, balance: teacher.balance + Number(settings.defaultSubReward || 1) } : teacher));
          }
          colorIndex++;
        } else if (existingSubId && (!assignment || !assignment.substitute)) {
          hasChanges = true;
          const docRef = doc(db, 'schools', activeSchoolId, 'lesson_substitutions', existingSubId);
          batch.delete(docRef);
          
          const existingRecord = substitutions.find(s => s.subId === existingSubId);
          if (existingRecord) {
            const balancesRef = doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances');
            batch.set(balancesRef, {
              [existingRecord.substituteStaffId]: increment(-Number(existingRecord.reward || 0))
            }, { merge: true });
            
            setTeachers(prev => prev.map(teacher => teacher.initial === existingRecord.substituteStaffId ? { ...teacher, balance: teacher.balance - Number(existingRecord.reward || 0) } : teacher));
          }
        }
      }
      
      if (hasChanges) {
        await batch.commit();
        alert(t('substitutionAdmin.assignmentsSaved'));
      } else {
        alert(t('substitutionAdmin.noSubstitutesAssigned'));
      }
    } catch (e) {
      alert(t('substitutionAdmin.assignFailed', { message: e.message }));
    }
    setSaving(false);
  };

  const tabs = [
    { id: 'statistics', label: t('substitutionAdmin.tabs.statistics'), icon: BarChart2 },
    { id: 'absence', label: t('substitutionAdmin.tabs.absence'), icon: UserMinus },
    { id: 'substitution', label: t('substitutionAdmin.tabs.substitution'), icon: UserCheck },
    { id: 'arrange', label: t('substitutionAdmin.tabs.arrange'), icon: CalendarIcon },
    { id: 'settings', label: t('substitutionAdmin.tabs.settings'), icon: Settings }
  ];

  const absentStaffOnArrangeDate = absences.filter(a => a.date === arrangeDate).map(a => a.staffId);
  const netBalanceMap = {};
  if (activeTab === 'arrange') {
    teachers.forEach(teacher => {
      netBalanceMap[teacher.initial] = teacher.balance || 0;
    });
  }

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>{t('substitutionAdmin.loading')}</div>;
  }

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
              >
                <div className="theme-tab-icon">
                  <Icon size={20} />
                </div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {activeTab === 'absence' && (
            <>
              <button className="btn-secondary" onClick={() => document.getElementById('import-absences').click()} disabled={saving} title={t('substitutionAdmin.importCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', width: 'auto' }}>
                <Upload size={16} />
              </button>
              <button className="btn-secondary" onClick={handleExportAbsence} title={t('substitutionAdmin.exportCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', width: 'auto' }}>
                <Download size={16} />
              </button>
              <button className="btn-primary" onClick={() => setShowAbsenceForm(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0 0.9rem', width: 'auto', whiteSpace: 'nowrap' }}>
                <Plus size={14} /> {t('substitutionAdmin.logAbsence')}
              </button>
            </>
          )}
          {activeTab === 'substitution' && (
            <>
              <button className="btn-secondary" onClick={() => document.getElementById('import-substitutions').click()} disabled={saving} title={t('substitutionAdmin.importCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', width: 'auto' }}>
                <Upload size={16} />
              </button>
              <button className="btn-secondary" onClick={handleExportSubstitution} title={t('substitutionAdmin.exportCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', width: 'auto' }}>
                <Download size={16} />
              </button>
              <button className="btn-primary" onClick={() => setShowDutyForm(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0 0.9rem', width: 'auto', whiteSpace: 'nowrap' }}>
                <Plus size={14} /> {t('substitutionAdmin.logExtraDuty')}
              </button>
            </>
          )}
        </div>
        </HeaderActionsPortal>
      </div>

      <input type="file" id="import-absences" accept=".csv" style={{ display: 'none' }} onChange={handleImportAbsence} />
      <input type="file" id="import-substitutions" accept=".csv" style={{ display: 'none' }} onChange={handleImportSubstitution} />

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', padding: isMobile ? '0.75rem' : '1.5rem' }}>
        {activeTab === 'statistics' && (() => {
          let lessonPeriods = [];
          if (profiles.length > 0) {
            const defProfile = profiles.find(p => p.isDefault) || profiles[0];
            if (defProfile && defProfile.periods) {
              lessonPeriods = defProfile.periods
                .map((p, idx) => ({ ...p, idx }))
                .filter(p => !p.type || !['recess', 'lunch', 'break'].includes(p.type.toLowerCase())); 
            }
          }

          const customFormattedDate2 = statsDate.includes('-') ? statsDate.split('-').reverse().join('/') : statsDate;

          const combinedStats = teachers.map(teacher => {
            const isAbsent = absences.some(a => a.staffId === teacher.initial && (a.date === statsDate || a.date === customFormattedDate2));
            const teacherLessonsToday = allTeacherLessons.filter(l => l.T === teacher.initial && l.P && l.P.startsWith(statsDayCode));
            const teacherSubstitutionsToday = substitutions.filter(s => s.substituteStaffId === teacher.initial && (s.date === statsDate || s.date === customFormattedDate2) && s.type === 'lesson');
            
            const netBal = teacher.balance || 0;
            const availability = {};
            const freePeriodNames = [];

            if (statsDayCode && lessonPeriods.length > 0) {
              lessonPeriods.forEach(p => {
                let periodSlot = (p.name || '').toString().replace(/\D/g, '');
                if (!periodSlot) periodSlot = p.idx;
                const periodCode = `${statsDayCode}${periodSlot}`;
                const teacherLesson = teacherLessonsToday.find(l => l.P === periodCode);
                const isSubstituting = teacherSubstitutionsToday.some(s => s.periodCode === periodCode);

                if (teacherLesson) {
                  availability[p.idx] = { type: 'regular', detail: `${teacherLesson.C || ''} ${teacherLesson.S || ''}`.trim(), room: (teacherLesson.R || '').trim() };
                } else {
                  if (isSubstituting) {
                    availability[p.idx] = { type: 'substituting', detail: 'Substituting' };
                  } else {
                    availability[p.idx] = { type: 'free', detail: 'Free' };
                  }
                  freePeriodNames.push((p.name || p.idx).toString().replace(/Lesson\s*/i, '').replace(/Morning\s*Assembly/i, '0').trim());
                }
              });
            }

            return {
              ...teacher,
              netBalance: netBal,
              availability,
              freePeriodNames,
              isAbsent
            };
          });

          // Sort by balance ASC and then initial ASC
          combinedStats.sort((a, b) => {
            if (a.netBalance !== b.netBalance) {
              return a.netBalance - b.netBalance;
            }
            return a.initial.localeCompare(b.initial);
          });

          const avgBal = combinedStats.length > 0 
            ? (combinedStats.reduce((sum, stat) => sum + stat.netBalance, 0) / combinedStats.length).toFixed(1) 
            : 0;

          return (
            <div className="animate-fade-in">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('substitutionAdmin.statsTitle')}</h2>
                  {/* Said where the work happens, not in a help page. Nobody discovers a gesture. */}
                  <span style={{ fontSize: '0.78rem', color: pickedLesson ? 'var(--color-accent)' : 'var(--color-text-muted)', fontWeight: pickedLesson ? '700' : '400' }}>
                    {pickedLesson
                      ? t('substitutionAdmin.tapPicked', { lesson: pickedLesson.lesson?.detail || t('substitutionAdmin.lesson') })
                      : t('substitutionAdmin.tapHint')}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {Object.keys(dragDropSubstitutions).length > 0 && (
                    <button className="btn-primary" onClick={handleSaveDragDrops} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 1rem' }}>
                      <Save size={16} /> {saving ? t('common.saving') : t('common.save')}
                    </button>
                  )}
                  <div style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-md)', fontWeight: '500', whiteSpace: 'nowrap' }}>
                    {t('substitutionAdmin.avgBalance')} <strong style={{ color: avgBal < 0 ? 'var(--color-danger)' : avgBal > 0 ? 'var(--color-success)' : 'var(--color-text)' }}>{avgBal > 0 ? '+' : ''}{avgBal}</strong>
                  </div>
                  <label style={{ fontWeight: '600', whiteSpace: 'nowrap' }}>{t('substitutionAdmin.selectDate')}</label>
                  <input type="date" className="input-field" value={statsDate} onChange={e => setStatsDate(e.target.value)} style={{ width: '150px', margin: 0 }} />
                  <div style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-md)', fontWeight: '500', whiteSpace: 'nowrap' }}>
                    {t('substitutionAdmin.dayCode')} <strong style={{ color: 'var(--color-primary)' }}>{statsDayCode || t('substitutionAdmin.notApplicable')}</strong>
                  </div>
                </div>
              </div>

              {!statsDayCode && (
                <div style={{ padding: '1rem', marginBottom: '1rem', backgroundColor: 'var(--color-warning-light)', color: 'var(--color-warning)', borderRadius: 'var(--radius-md)' }}>
                  {t('substitutionAdmin.noCalendarEvent')}
                </div>
              )}

              <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflowX: 'auto', marginBottom: '2rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: '800px' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)', width: '60px' }}>{t('substitutionAdmin.staff')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: '600', borderBottom: '1px solid var(--color-border)', width: '120px' }}>{t('substitutionAdmin.balance')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)', width: '200px' }}>{t('substitutionAdmin.free')}</th>
                      {statsDayCode && lessonPeriods.map(p => (
                        <th key={p.idx} style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600', borderBottom: '1px solid var(--color-border)', borderLeft: '1px solid var(--color-border)' }}>
                          {(p.name || p.idx).toString().replace(/Lesson\s*/i, '').replace(/Morning\s*Assembly/i, '0').trim()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {combinedStats.map((stat, idx) => {
                      const rowBg = stat.isAbsent ? 'var(--color-warning-bg)' : 'transparent';
                      return (
                      <tr key={stat.uid} style={{ 
                        borderBottom: idx < combinedStats.length - 1 ? '1px solid var(--color-border)' : 'none'
                      }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '500', backgroundColor: rowBg }}>{stat.initial}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'center', backgroundColor: rowBg }}>
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '1rem', 
                            fontWeight: '600',
                            backgroundColor: stat.netBalance > 0 ? 'var(--color-success-light)' : stat.netBalance < 0 ? 'var(--color-danger-light)' : 'var(--color-bg-app)',
                            color: stat.netBalance > 0 ? 'var(--color-success)' : stat.netBalance < 0 ? 'var(--color-danger)' : 'var(--color-text)'
                          }}>
                            {stat.netBalance > 0 ? '+' : ''}{stat.netBalance}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)', backgroundColor: rowBg, fontSize: '0.75rem', lineHeight: '1.2' }}>
                          {statsDayCode ? stat.freePeriodNames.join(', ') || '-' : '-'}
                        </td>
                        {statsDayCode && lessonPeriods.map(p => {
                          const statusObj = stat.availability[p.idx] || { type: 'free', detail: '' };
                          const status = statusObj.type;
                          
                          // Check for drag-drop override
                          let isDragSource = false;
                          let dragSourceColor = '';
                          let isDragTarget = false;
                          let dragTargetLesson = null;
                          let dragTargetColor = '';
                          
                          const sourceKey = `${stat.initial}_${p.idx}`;
                          if (dragDropSubstitutions[sourceKey]) {
                            isDragSource = true;
                            dragSourceColor = dragDropSubstitutions[sourceKey].color;
                          }
                          
                          // Check if this cell is a target
                          let originalSourceId = null;
                          Object.keys(dragDropSubstitutions).forEach(key => {
                            const sub = dragDropSubstitutions[key];
                            if (sub.substituteId === stat.initial && key.endsWith(`_${p.idx}`)) {
                              isDragTarget = true;
                              dragTargetLesson = sub.lesson;
                              dragTargetColor = sub.color;
                              originalSourceId = key.split('_')[0];
                            }
                          });

                          // Check if this cell is a saved target
                          let periodSlot = (p.name || '').toString().replace(/\D/g, '');
                          if (!periodSlot) periodSlot = p.idx;

                          let isSavedTarget = false;
                          let savedTargetLesson = null;
                          let savedTargetColor = '';
                          const savedSub = substitutions.find(s => 
                            s.substituteStaffId === stat.initial && 
                            s.date === statsDate && 
                            s.periodCode === `${statsDayCode}${periodSlot}`
                          );
                          if (savedSub) {
                            isSavedTarget = true;
                            savedTargetLesson = { detail: `${savedSub.class} ${savedSub.subj}`, room: savedSub.room };
                            savedTargetColor = savedSub.color || substitutionColors[(savedSub.subId.charCodeAt(0) + savedSub.subId.charCodeAt(savedSub.subId.length - 1)) % substitutionColors.length] || 'var(--color-info-bg)';
                          }

                          // Check if this cell is a saved source
                          let isSavedSource = false;
                          let savedSourceColor = '';
                          const savedSourceSub = substitutions.find(s => 
                            s.absentStaffId === stat.initial && 
                            s.date === statsDate && 
                            s.periodCode === `${statsDayCode}${periodSlot}`
                          );
                          if (savedSourceSub) {
                            isSavedSource = true;
                            savedSourceColor = savedSourceSub.color || substitutionColors[(savedSourceSub.subId.charCodeAt(0) + savedSourceSub.subId.charCodeAt(savedSourceSub.subId.length - 1)) % substitutionColors.length] || 'var(--color-info-bg)';
                          }

                          let bgColor = 'transparent';
                          if (isDragSource) bgColor = dragSourceColor;
                          else if (isDragTarget) bgColor = dragTargetColor;
                          else if (isSavedSource) bgColor = savedSourceColor;
                          else if (isSavedTarget) bgColor = savedTargetColor;
                          else if (status === 'free') bgColor = 'transparent'; // No more green
                          else if (status === 'substituting') bgColor = 'var(--color-warning)';
                          else if (status === 'regular') bgColor = 'var(--color-danger)';
                          
                          const isDraggable = (stat.isAbsent && status === 'regular' && !isSavedSource) || isDragTarget;
                          const isDroppable = (!stat.isAbsent && (status === 'free' || status === 'substituting') && !isDragTarget && !isDragSource) || isDragSource;

                          // The same payload the drag puts on the clipboard, so a tap and a drop
                          // hand the placement identical data.
                          const tapData = {
                            sourceTeacherId: isDragTarget ? originalSourceId : stat.initial,
                            periodIdx: p.idx,
                            lesson: isDragTarget ? dragTargetLesson : statusObj,
                          };
                          const isPicked = !!pickedLesson
                            && pickedLesson.sourceTeacherId === tapData.sourceTeacherId
                            && pickedLesson.periodIdx === p.idx;
                          // Only the same period can take it, so only those cells are offered --
                          // the drag has to say no after the fact, but a tap can simply not invite.
                          const isTapTarget = !!pickedLesson && !isPicked && isDroppable
                            && pickedLesson.periodIdx === p.idx;
                          
                          const showLessonDetails = status === 'regular';
                          const showTargetDetails = (isDragTarget && dragTargetLesson) || (isSavedTarget && savedTargetLesson);
                          const targetLessonToDisplay = isDragTarget ? dragTargetLesson : (isSavedTarget ? savedTargetLesson : null);

                          return (
                            <td 
                              key={p.idx} 
                              style={{ padding: 0, borderLeft: '1px solid var(--color-border)', backgroundColor: rowBg, maxWidth: '60px' }}
                              onDragOver={isDroppable ? (e) => handleDragOver(e) : undefined}
                              onDrop={isDroppable ? (e) => handleDrop(e, stat.initial, p.idx) : undefined}
                              onClick={(isDraggable || isTapTarget)
                                ? () => handleCellTap({ isDraggable, isDroppable, data: tapData, teacherId: stat.initial, periodIdx: p.idx })
                                : undefined}
                            >
                              <div 
                                style={{ 
                                  height: '32px', 
                                  width: '100%', 
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.65rem',
                                  color: (isDragSource || isDragTarget || isSavedSource || isSavedTarget) ? 'var(--color-text-main)' : 'white', // Dark text for pastel drag colors
                                  fontWeight: '600',
                                  overflow: 'hidden',
                                  padding: '0 2px',
                                  lineHeight: '1.2',
                                  cursor: (isDraggable || isTapTarget) ? 'pointer' : 'default',
                                  opacity: isDragSource ? 0.6 : 1,
                                  // The lesson in hand, and the cells that will take it. Inset so
                                  // the outline does not move anything by 2px when it appears.
                                  boxShadow: isPicked
                                    ? 'inset 0 0 0 2px var(--color-accent)'
                                    : (isTapTarget ? 'inset 0 0 0 2px var(--color-info)' : 'none'),
                                  // An empty target has nothing to show but its own outline.
                                  backgroundColor: (isTapTarget && bgColor === 'transparent')
                                    ? 'var(--color-info-bg)' : bgColor,
                                }} 
                                title={showLessonDetails ? (statusObj.room ? `${statusObj.detail} ${statusObj.room}` : statusObj.detail) : (showTargetDetails && targetLessonToDisplay ? `${targetLessonToDisplay.detail} ${targetLessonToDisplay.room}` : '')}
                                draggable={isDraggable}
                                onDragStart={isDraggable ? (e) => handleDragStart(e, isDragTarget ? originalSourceId : stat.initial, p.idx, isDragTarget ? dragTargetLesson : statusObj) : undefined}
                              >
                                {showLessonDetails && (
                                  <>
                                    <div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', width: '100%', textAlign: 'center' }}>{statusObj.detail}</div>
                                    <div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', width: '100%', textAlign: 'center', opacity: 0.9 }}>{statusObj.room}</div>
                                  </>
                                )}
                                {showTargetDetails && targetLessonToDisplay && (
                                  <>
                                    <div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', width: '100%', textAlign: 'center' }}>{targetLessonToDisplay.detail}</div>
                                    <div style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', width: '100%', textAlign: 'center', opacity: 0.9 }}>{targetLessonToDisplay.room}</div>
                                  </>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    )})}
                    {combinedStats.length === 0 && (
                      <tr><td colSpan={3 + (statsDayCode ? lessonPeriods.length : 0)} style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('substitutionAdmin.noStaffData')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
        {activeTab === 'absence' && (
          <div className="animate-fade-in">
            {showAbsenceForm && (
            <div
              onClick={() => setShowAbsenceForm(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 120,
                backgroundColor: 'var(--color-overlay)',
                display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
                padding: isMobile ? 0 : '2rem',
              }}
            >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                backgroundColor: 'var(--color-bg-card)',
                borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                boxShadow: 'var(--shadow-lg)',
                width: '100%', maxWidth: isMobile ? '100%' : '820px',
                maxHeight: isMobile ? '92vh' : '90vh',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
                <h2 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('substitutionAdmin.logAbsenceTitle')}</h2>
                <button type="button" onClick={() => setShowAbsenceForm(false)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleLogAbsence} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '1rem', padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto' }}>
              <div style={{ flex: '1 1 120px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('substitutionAdmin.date')}</label>
                <input type="date" className="input-field" value={absenceForm.date} onChange={e => setAbsenceForm({...absenceForm, date: e.target.value})} required style={{ margin: 0 }} />
              </div>
              <div style={{ flex: '2 1 180px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('substitutionAdmin.staff')}</label>
                <select className="input-field" value={absenceForm.staffId} onChange={e => setAbsenceForm({...absenceForm, staffId: e.target.value})} required style={{ margin: 0 }}>
                  <option value="">{t('substitutionAdmin.selectStaff')}</option>
                  {teachers.map(teacher => <option key={teacher.uid} value={teacher.initial}>{teacher.name}</option>)}
                </select>
              </div>
              <div style={{ flex: '2 1 180px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('substitutionAdmin.reason')}</label>
                <select className="input-field" value={absenceForm.reasonId} onChange={handleReasonChange} required style={{ margin: 0 }}>
                  <option value="">{t('substitutionAdmin.selectReason')}</option>
                  {settings.absenceReasons.map(r => <option key={r.id} value={r.id}>{r.reason}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 100px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('substitutionAdmin.deductAmount')}</label>
                <input type="number" step="0.5" className="input-field" value={absenceForm.deduction} onChange={e => setAbsenceForm({...absenceForm, deduction: Number(e.target.value)})} required style={{ margin: 0 }} />
              </div>
              <div style={{ flex: '3 1 200px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('substitutionAdmin.remarks')}</label>
                <input type="text" className="input-field" value={absenceForm.remarks} onChange={e => setAbsenceForm({...absenceForm, remarks: e.target.value})} placeholder={t('substitutionAdmin.optionalRemarks')} style={{ margin: 0 }} />
              </div>
              <div>
                <button type="submit" className="btn-primary" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', height: '42px', padding: '0 1.25rem', whiteSpace: 'nowrap' }}>
                  <Plus size={16} /> {saving ? '...' : t('common.submit')}
                </button>
              </div>
            </form>
            </div>
            </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>{t('substitutionAdmin.recentAbsences')}</h3>
              <select className="input-field" value={timeRange} onChange={e => setTimeRange(e.target.value)} style={{ margin: 0, width: 'auto', padding: '0.4rem 2rem 0.4rem 0.75rem' }}>
                <option value="5days">{t('substitutionAdmin.last5Days')}</option>
                <option value="month">{t('substitutionAdmin.currentMonth')}</option>
                <option value="all">{t('substitutionAdmin.allTime')}</option>
              </select>
            </div>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.date')}</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.staff')}</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.reason')}</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.deduction')}</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.remarks')}</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {absences.map((abs, idx) => {
                    const staff = teachers.find(teacher => teacher.initial === abs.staffId) || { name: abs.staffId };
                    const reason = settings.absenceReasons.find(r => r.id === abs.reasonId) || { reason: abs.reasonId };
                    return (
                      <tr key={abs.absId} style={{ borderBottom: idx < absences.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                        <td style={{ padding: '0.75rem 1rem' }}>{abs.date}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{abs.staffId}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{reason.reason}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span style={{ color: 'var(--color-danger)', fontWeight: '500' }}>-{abs.deduction}</span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>{abs.remarks || '-'}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                          <button className="btn-icon" onClick={() => handleDeleteAbsence(abs)} style={{ color: 'var(--color-danger)' }} title={t('common.delete')}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {absences.length === 0 && (
                    <tr><td colSpan="6" style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('substitutionAdmin.noAbsences')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === 'substitution' && (
          <div className="animate-fade-in">
            {showDutyForm && (
            <div
              onClick={() => setShowDutyForm(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 120,
                backgroundColor: 'var(--color-overlay)',
                display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
                padding: isMobile ? 0 : '2rem',
              }}
            >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                backgroundColor: 'var(--color-bg-card)',
                borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                boxShadow: 'var(--shadow-lg)',
                width: '100%', maxWidth: isMobile ? '100%' : '820px',
                maxHeight: isMobile ? '92vh' : '90vh',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
                <h2 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('substitutionAdmin.logDutyTitle')}</h2>
                <button type="button" onClick={() => setShowDutyForm(false)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleLogDuty} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '1rem', padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto' }}>
              <div style={{ flex: '1 1 120px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('substitutionAdmin.date')}</label>
                <input type="date" className="input-field" value={dutyForm.date} onChange={e => setDutyForm({...dutyForm, date: e.target.value})} required style={{ margin: 0 }} />
              </div>
              <div style={{ flex: '2 1 180px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('substitutionAdmin.staff')}</label>
                <select className="input-field" value={dutyForm.staffId} onChange={e => setDutyForm({...dutyForm, staffId: e.target.value})} required style={{ margin: 0 }}>
                  <option value="">{t('substitutionAdmin.selectStaff')}</option>
                  {teachers.map(teacher => <option key={teacher.uid} value={teacher.initial}>{teacher.name}</option>)}
                </select>
              </div>
              <div style={{ flex: '2 1 180px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('substitutionAdmin.type')}</label>
                <select className="input-field" value={dutyForm.typeId} onChange={e => {
                  const tId = e.target.value;
                  const tObj = (settings.dutyTypes || []).find(teacher => teacher.id === tId);
                  setDutyForm({...dutyForm, typeId: tId, reward: tObj ? tObj.defaultReward : settings.defaultSubReward});
                }} required style={{ margin: 0 }}>
                  {(settings.dutyTypes || [{id: 'extra_duty', type: 'Extra Duty'}]).map(teacher => (
                    <option key={teacher.id} value={teacher.id}>{teacher.type}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '1 1 100px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('substitutionAdmin.reward')}</label>
                <input type="number" step="0.5" className="input-field" value={dutyForm.reward} onChange={e => setDutyForm({...dutyForm, reward: Number(e.target.value)})} required style={{ margin: 0 }} />
              </div>
              <div style={{ flex: '3 1 200px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('substitutionAdmin.description')}</label>
                <input type="text" className="input-field" value={dutyForm.remarks} onChange={e => setDutyForm({...dutyForm, remarks: e.target.value})} placeholder={t('substitutionAdmin.dutyPlaceholder')} required style={{ margin: 0 }} />
              </div>
              <div>
                <button type="submit" className="btn-primary" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', height: '42px', padding: '0 1.25rem', whiteSpace: 'nowrap' }}>
                  <Plus size={16} /> {saving ? '...' : t('common.submit')}
                </button>
              </div>
            </form>
            </div>
            </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>{t('substitutionAdmin.historyTitle')}</h3>
              <select className="input-field" value={timeRange} onChange={e => setTimeRange(e.target.value)} style={{ margin: 0, width: 'auto', padding: '0.4rem 2rem 0.4rem 0.75rem' }}>
                <option value="5days">{t('substitutionAdmin.last5Days')}</option>
                <option value="month">{t('substitutionAdmin.currentMonth')}</option>
                <option value="all">{t('substitutionAdmin.allTime')}</option>
              </select>
            </div>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.date')}</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.staff')}</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.type')}</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.details')}</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.reward')}</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {substitutions.map((sub, idx) => {
                    const staff = teachers.find(teacher => teacher.initial === sub.substituteStaffId) || { name: sub.substituteStaffId };
                    const isLesson = sub.type === 'lesson';
                    const dutyTypeObj = !isLesson ? (settings.dutyTypes || []).find(d => d.id === sub.type) : null;
                    // The school names its own duty types; these two are only the fallback.
                    const dutyTypeName = dutyTypeObj ? dutyTypeObj.type
                      : (isLesson ? t('substitutionAdmin.lessonSub') : t('substitutionAdmin.extraDuty'));
                    const details = isLesson
                      ? t('substitutionAdmin.substitutedDetail', {
                        class: sub.class, subject: sub.subj, room: sub.room,
                        start: sub.start_time, end: sub.end_time,
                      })
                      : sub.remarks;
                    
                    return (
                      <tr key={sub.subId} style={{ borderBottom: idx < substitutions.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                        <td style={{ padding: '0.75rem 1rem' }}>{sub.date}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{sub.substituteStaffId}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span style={{ 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '1rem', 
                            fontSize: '0.75rem', 
                            fontWeight: '600',
                            backgroundColor: isLesson ? 'var(--color-primary-light)' : 'var(--color-success-light)',
                            color: isLesson ? 'var(--color-primary)' : 'var(--color-success)'
                          }}>
                            {dutyTypeName}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>{details || '-'}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span style={{ color: 'var(--color-success)', fontWeight: '500' }}>+{sub.reward}</span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                          <button className="btn-icon" onClick={() => handleDeleteSub(sub)} style={{ color: 'var(--color-danger)' }} title={t('common.delete')}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {substitutions.length === 0 && (
                    <tr><td colSpan="6" style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('substitutionAdmin.noSubRecords')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === 'arrange' && (() => {
          const visibleOrphanedLessons = orphanedLessons.filter(l => !hiddenLessons.includes(l.id));
          return (
          <div className="animate-fade-in">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
                <label style={{ fontWeight: '600', whiteSpace: 'nowrap' }}>{t('substitutionAdmin.selectDate')}</label>
                <input type="date" className="input-field" value={arrangeDate} onChange={e => setArrangeDate(e.target.value)} style={{ width: '150px', margin: 0 }} />
                <div style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-md)', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  {t('substitutionAdmin.day')} <strong style={{ color: 'var(--color-primary)' }}>{dayCode || t('substitutionAdmin.notApplicable')}</strong>
                </div>
                <div style={{ flex: 1 }}></div>
                {visibleOrphanedLessons.length > 0 && dayCode && (
                  <button className="btn-primary" onClick={handleSaveAllAssignments} disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.5rem', whiteSpace: 'nowrap', width: 'auto' }}>
                    <Save size={16} /> {saving ? t('common.saving') : t('substitutionAdmin.saveAll')}
                  </button>
                )}
              </div>

            {!dayCode ? (
              <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-muted)' }}>
                {t('substitutionAdmin.noCalendarEventArrange')}
              </div>
            ) : visibleOrphanedLessons.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-muted)' }}>
                {t('substitutionAdmin.noOrphanedLessons')}
              </div>
            ) : (
              <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: '600px' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.initial')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.period')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.className')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.subject')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.room')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.substitute')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...visibleOrphanedLessons].sort((a, b) => {
                      const pA = parseInt((a.P || '').replace(/\D/g, '') || 0);
                      const pB = parseInt((b.P || '').replace(/\D/g, '') || 0);
                      if (pA !== pB) return pA - pB;
                      return (a.C || '').localeCompare(b.C || '');
                    }).map((lesson, idx) => {
                      
                      const availableSubstitutes = teachers.filter(teacher => {
                        if (teacher.initial === lesson.T) return false;
                        if (absentStaffOnArrangeDate.includes(teacher.initial)) return false;
                        const hasRegularLesson = allTeacherLessons.some(l => l.T === teacher.initial && l.P === lesson.P);
                        if (hasRegularLesson) return false;
                        return true;
                      }).sort((a, b) => {
                        const balDiff = netBalanceMap[a.initial] - netBalanceMap[b.initial];
                        if (balDiff !== 0) return balDiff;
                        return a.initial.localeCompare(b.initial);
                      });

                      const assignment = substituteAssignments[lesson.id] || { substitute: '', room: lesson.R || '' };

                      return (
                        <tr key={lesson.id} style={{ borderBottom: idx < visibleOrphanedLessons.length - 1 ? '1px solid var(--color-border)' : 'none', backgroundColor: 'var(--color-bg-card)' }}>
                          <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: 'var(--color-danger)' }}>{lesson.T}</td>
                          <td style={{ padding: '0.75rem 1rem', fontWeight: '500' }}>{lesson.P ? lesson.P.replace(/\D/g, '') : '-'}</td>
                          <td style={{ padding: '0.75rem 1rem' }}>{lesson.C || '-'}</td>
                          <td style={{ padding: '0.75rem 1rem' }}>{lesson.S || '-'}</td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            <select
                              className="input-field"
                              style={{ margin: 0, padding: '0.4rem 0.5rem', width: '100px' }}
                              value={assignment.room}
                              onChange={e => setSubstituteAssignments({...substituteAssignments, [lesson.id]: { ...assignment, room: e.target.value }})}
                            >
                              <option value="">{t('substitutionAdmin.room')}</option>
                              {rooms.map(r => <option key={r.code} value={r.code}>{r.code}</option>)}
                              {lesson.R && !rooms.find(r => r.code === lesson.R) && <option value={lesson.R}>{lesson.R}</option>}
                            </select>
                          </td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            <select 
                              className="input-field" 
                              style={{ margin: 0, padding: '0.4rem 0.5rem', width: '150px' }}
                              value={assignment.substitute}
                              onChange={e => setSubstituteAssignments({...substituteAssignments, [lesson.id]: { ...assignment, substitute: e.target.value }})}
                            >
                              <option value="">{t('substitutionAdmin.selectSubstitute')}</option>
                              {availableSubstitutes.map(teacher => (
                                <option key={teacher.uid} value={teacher.initial}>{teacher.initial}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                            <button className="btn-icon" onClick={() => handleIgnoreLesson(lesson)} style={{ color: 'var(--color-danger)' }} title={t('substitutionAdmin.removeFromList')}>
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          );
        })()}
        
        {activeTab === 'settings' && (
          <div className="animate-fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t('substitutionAdmin.settingsTitle')}</h2>
              <button className="btn-primary" onClick={handleSaveSettings} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Save size={16} /> {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>{t('substitutionAdmin.generalSettings')}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <label style={{ fontWeight: '500' }}>{t('substitutionAdmin.defaultReward')}</label>
                <input 
                  type="number" 
                  className="input-field" 
                  style={{ width: '100px', margin: 0 }}
                  value={settings.defaultSubReward}
                  onChange={e => setSettings({...settings, defaultSubReward: Number(e.target.value)})}
                />
                <span style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>(Balance awarded for substituting a lesson or performing extra duty)</span>
              </div>
            </div>

            <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--color-bg-app)', padding: '1.5rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: '0 0 0.5rem 0' }}>{t('substitutionAdmin.recalcTitle')}</h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>{t('substitutionAdmin.recalcIntro')}</p>
              </div>
              <button className="btn-secondary" onClick={async () => {
                if (!window.confirm(t('substitutionAdmin.recalcConfirm'))) return;
                setSaving(true);
                try {
                  const absSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'staff_absences'));
                  const subSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'lesson_substitutions'));
                  
                  const balanceMap = {};
                  absSnap.forEach(d => {
                    const data = d.data();
                    const initial = data.staffId;
                    if (initial) balanceMap[initial] = (balanceMap[initial] || 0) - (Number(data.deduction) || 0);
                  });
                  subSnap.forEach(d => {
                    const data = d.data();
                    const initial = data.substituteStaffId;
                    if (initial) balanceMap[initial] = (balanceMap[initial] || 0) + (Number(data.reward) || 0);
                  });
                  
                  await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'substitution_balances'), balanceMap);
                  setTeachers(prev => prev.map(teacher => ({ ...teacher, balance: balanceMap[teacher.initial] || 0 })));
                  alert(t('substitutionAdmin.recalcDone'));
                } catch (err) {
                  alert(t('substitutionAdmin.recalcFailed', { message: err.message }));
                }
                setSaving(false);
              }} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Repeat size={16} /> 
                Recalculate Now
              </button>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>{t('substitutionAdmin.reasonsTitle')}</h3>
                <button className="btn-outline" onClick={handleAddAbsenceReason} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}>
                  <Plus size={16} /> Add Reason
                </button>
              </div>

              <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.reason')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.defaultDeduction')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settings.absenceReasons.map((reason, idx) => (
                      <tr key={reason.id} style={{ borderBottom: idx < settings.absenceReasons.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                        <td style={{ padding: '0.5rem 1rem' }}>
                          <input 
                            type="text" 
                            className="input-field" 
                            style={{ margin: 0 }}
                            value={reason.reason}
                            onChange={e => handleUpdateAbsenceReason(reason.id, 'reason', e.target.value)}
                          />
                        </td>
                        <td style={{ padding: '0.5rem 1rem' }}>
                          <input 
                            type="number" 
                            className="input-field" 
                            style={{ margin: 0, width: '100px' }}
                            value={reason.defaultDeduction}
                            onChange={e => handleUpdateAbsenceReason(reason.id, 'defaultDeduction', Number(e.target.value))}
                          />
                        </td>
                        <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                          <button 
                            className="btn-icon" 
                            onClick={() => handleRemoveAbsenceReason(reason.id)}
                            style={{ color: 'var(--color-danger)' }}
                            title={t('substitutionAdmin.removeReason')}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {settings.absenceReasons.length === 0 && (
                      <tr><td colSpan="3" style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('substitutionAdmin.noReasons')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>{t('substitutionAdmin.dutyTypesTitle')}</h3>
                <button className="btn-outline" onClick={handleAddDutyType} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}>
                  <Plus size={16} /> Add Type
                </button>
              </div>

              <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.dutyType')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.defaultRewardCol')}</th>
                      <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', borderBottom: '1px solid var(--color-border)' }}>{t('substitutionAdmin.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(settings.dutyTypes || []).map((duty, idx) => (
                      <tr key={duty.id} style={{ borderBottom: idx < (settings.dutyTypes || []).length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                        <td style={{ padding: '0.5rem 1rem' }}>
                          <input 
                            type="text" 
                            className="input-field" 
                            style={{ margin: 0 }}
                            value={duty.type}
                            onChange={e => handleUpdateDutyType(duty.id, 'type', e.target.value)}
                          />
                        </td>
                        <td style={{ padding: '0.5rem 1rem' }}>
                          <input 
                            type="number" 
                            step="0.5"
                            className="input-field" 
                            style={{ margin: 0, width: '100px' }}
                            value={duty.defaultReward}
                            onChange={e => handleUpdateDutyType(duty.id, 'defaultReward', Number(e.target.value))}
                          />
                        </td>
                        <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                          <button 
                            className="btn-icon" 
                            onClick={() => handleRemoveDutyType(duty.id)}
                            style={{ color: 'var(--color-danger)' }}
                            title={t('substitutionAdmin.removeDutyType')}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(!settings.dutyTypes || settings.dutyTypes.length === 0) && (
                      <tr><td colSpan="3" style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('substitutionAdmin.noDutyTypes')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubstitutionAdmin;
